import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const UpPricing = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedService, setSelectedService] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const Navigate = useNavigate()
  const services = [
    {
      id: 'cash',
      category: "Cash Segment",
      icon: "💵",
      services: [
        {
          name: "Wealth Wave Pack",
          description: "Long-term holding strategy",
          holding: "3-6 months holding",
          features: ["Curated stock selection", "Weekly analysis", "Risk management"],
          pricing: {
            Quarterly: "30,000"
          }
        },
        {
          name: "Trade Nexus Pack",
          description: "Short-term trading opportunities",
          holding: "5-15 days holding",
          features: ["Daily trade ideas", "Technical analysis", "Entry/exit points"],
          pricing: {
            Monthly: "14,999",
            Quarterly: "41,000"
          }
        },
        {
          name: "Cash MIS",
          description: "Short-term trading opportunities",
          holding: "Intraday positions",
          features: ["Daily trade ideas", "Technical analysis", "Entry/exit points"],
          pricing: {
            Monthly: "14,999",
            Quarterly: "41,000"
          }
        }
      ]
    },
    {
      id: 'futures',
      category: "Future Segment",
      icon: "📈",
      services: [
        {
          name: "Stock Futures MIS",
          description: "Intraday futures trading",
          holding: "Intraday positions",
          features: ["1-2 daily calls", "Leverage strategies", "Risk controls"],
          pricing: {
            Monthly: "14,999",
            Quarterly: "41,000"
          }
        },
        {
          name: "Futures Combo",
          description: "Comprehensive futures package",
          holding: "Intraday positions",
          features: ["2-4 daily calls", "Stock & index futures", "Advanced analytics"],
          pricing: {
            Monthly: "22,000",
            Quarterly: "39,500"
          }
        }
      ]
    },
    {
      id: 'options',
      category: "Option Segment",
      icon: "⚙️",
      services: [
        {
          name: "Stock Options",
          description: "Options trading strategies",
          holding: "Intraday positions",
          features: ["2-3 daily calls", "Premium analysis", "Strategy guidance"],
          pricing: {
            Weekly: "6,000",
            Quarterly: "35,500"
          }
        },
        {
          name: "Index Options",
          description: "Nifty/BankNifty strategies",
          holding: "Intraday positions",
          features: ["2-3 daily calls", "Volatility analysis", "Hedging techniques"],
          pricing: {
            Weekly: "7,000",
            Quarterly: "35,500"
          }
        },
        {
          name: "Combo (Stock & Index)",
          description: "Nifty/BankNifty/Options",
          holding: "Intraday positions",
          features: ["2-3 daily calls", "Volatility analysis", "Hedging techniques"],
          pricing: {
            Quarterly: "35,500"
          }
        }
      ]
    },
    {
      id: 'commodity',
      category: "Commodity Segment",
      icon: "🛢️",
      services: [
        {
          name: "MCX Basic",
          description: "Metals & energy trading",
          holding: "Intraday positions",
          features: ["1-2 daily calls", "Commodity trends", "Global correlation"],
          pricing: {
            Weekly: "12,000",
            Quarterly: "37,000"
          }
        },
        {
          name: "MCX Premium",
          description: "Full commodity suite",
          holding: "Intraday positions",
          features: ["2-3 daily calls", "All segments", "Advanced charts"],
          pricing: {
            Weekly: "14,999",
            Quarterly: "39,500"
          }
        }
      ]
    }
  ];

  const filteredServices = activeTab === 'all'
    ? services
    : services.filter(service => service.id === activeTab);

  const openModal = (service, category, icon) => {
    setSelectedService({ ...service, category, icon });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-800 to-green-600 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl font-bold mb-4"
          >
            Advanced Trading Solutions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-green-100 max-w-3xl mx-auto"
          >
            Professional-grade trading packages tailored for every market condition
          </motion.p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          <button
            onClick={() => setActiveTab('all')}
            className={`px-6 py-2 rounded-full font-medium transition-all ${activeTab === 'all' ? 'bg-green-600 text-white shadow-md' : 'bg-white text-green-800 border border-green-200 hover:bg-green-50'}`}
          >
            All Services
          </button>
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => setActiveTab(service.id)}
              className={`px-6 py-2 rounded-full font-medium transition-all flex items-center ${activeTab === service.id ? 'bg-green-600 text-white shadow-md' : 'bg-white text-green-800 border border-green-200 hover:bg-green-50'}`}
            >
              <span className="mr-2">{service.icon}</span>
              {service.category}
            </button>
          ))}
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredServices.flatMap(category =>
            category.services.map((service, idx) => (
              <motion.div
                key={`${category.id}-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{service.name}</h3>
                      <p className="text-green-600 font-medium mt-1">{service.description}</p>
                    </div>
                    <span className="text-2xl">{category.icon}</span>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {service.holding}
                    </div>

                    <ul className="mt-4 space-y-2">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-sm text-gray-700">
                          <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 pt-4 border-t border-gray-100">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Starting from</div>
                      <div className="flex items-end justify-between">
                        <div>
                          <span className="text-2xl font-bold text-gray-900">₹{Object.values(service.pricing)[0]}</span>
                          <span className="text-sm text-gray-500 ml-1">/{Object.keys(service.pricing)[0]}</span>
                        </div>
                        <button
                          onClick={() => openModal(service, category.category, category.icon)}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          View Plans
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </main>

      {/* Pricing Modal */}
      {showModal && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-700 to-green-600 p-6 flex items-start justify-between">
              <div className="flex items-start">
                <span className="text-2xl mr-3 mt-1">{selectedService.icon}</span>
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedService.category}</h3>
                  <h4 className="text-lg text-green-100">{selectedService.name}</h4>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-green-100 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="mb-6">
                <h5 className="text-lg font-semibold text-gray-900 mb-2">Service Details</h5>
                <p className="text-gray-700">{selectedService.description}</p>
                <div className="flex items-center mt-3 text-sm text-green-600">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {selectedService.holding}
                </div>
              </div>

              <div className="mb-8">
                <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Pricing Plans
                </h5>

                <div className="space-y-3">
                  {Object.entries(selectedService.pricing).map(([plan, price]) => (
                    <div
                      key={plan}
                      className={`p-4 rounded-lg border ${plan === 'Quarterly' ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium text-gray-900">{plan}</span>
                          {plan === 'Quarterly' && (
                            <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">Best Value</span>
                          )}
                        </div>
                        <span className="font-bold text-green-700">₹{price}</span>
                      </div>
                      {plan !== 'Weekly' && (
                        <div className="mt-2 text-xs text-gray-500">
                          Save {plan === 'Monthly' ? '10%' : plan === 'Quarterly' ? '15%' : '20%'} compared to weekly billing
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <button className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium py-3 px-4 rounded-lg transition duration-300" onClick={() => { Navigate("/payment") }}>
                  Subscribe Now
                </button>
                <button className="w-full border border-green-600 text-green-600 hover:bg-green-50 font-medium py-3 px-4 rounded-lg transition duration-300" onClick={() => { Navigate("/contactus") }}>
                  Contact RM
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Consultation CTA */}
      <section className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Elevate Your Trading?</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto">
            Get personalized recommendations from our trading specialists to maximize your market potential.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors" onClick={() => { Navigate('/contactus') }}>
              Book Free Consultation
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UpPricing;